import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Mutation,
  MutationCache,
  Query,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  QueryKey,
  useMutationState,
} from '@tanstack/react-query'
import { auth } from '../firebase'
import toast, { Toaster } from 'react-hot-toast'
import { logErrorToDiscord } from '../services/discord'
import { translateAuthErrors } from '../helpers'
import { query } from 'firebase/firestore'

type Props = {
  children: React.ReactNode
}

type Toast = {
  icon?: string
  message: string
  buttons?: { text: string; role: string; handler: () => void }[]
  duration?: number
}

const QueryClientWithErrorHandlingProvider = (props: Props) => {
  //const [toast, setToast] = useState<Toast | null>(null)
  const retryingRef = useRef(0)
  const toastDuration = 2000

  // these queries show errors in the UI, not a toast
  const suppressToastErrorsForQueries = [
    'userData',
    'gymMemberData',
    'gymSubscription',
    'gymData',
    'notificationsRegistration',
  ]

  const notUsersFaultErrors = [401, 403, 404, 409, 429, 500, 502, 503, 504]
  const maxQueryRetries = 5

  const getErrorOrSuccessMessageForDiscordLogging = ({
    error = null,
    success = false,
    humanMessage,
    isSuppressed = false,
  }: {
    error?: any
    humanMessage?: string
    success?: boolean
    isSuppressed?: boolean
    userID?: string
  }) => {
    const requestURL = error?.config?.url || 'no URL'
    const retryCount = retryingRef.current
    const userName = auth.currentUser?.displayName || 'Unkown'
    const userID = auth.currentUser?.uid || 'Unknown'

    const message = !success
      ? `QueryClient Error\nRetries: ${retryCount}\nRequest URL: ${requestURL}\nUser: ${userName} (${userID})\nSuppressed for user: ${isSuppressed}\nMessage: ${
          error.message
        }${humanMessage && `\nHuman-readable message: ${humanMessage}`}\n`
      : `QueryClient Retry Success\nRetries: ${retryCount}\nRequest URL: ${requestURL}\nUser: ${userName} (${userID})\nSuppressed for user: ${isSuppressed}\n`

    return message
  }

  const showRetryRequestToast = (
    errorMessage: string,
    retryFunction: () => void,
    mutation?: Mutation
  ) => {
    toast.error(
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1',
          marginLeft: '8px',
        }}
      >
        <div>{errorMessage}</div>
        <div
          style={{
            display: 'flex',
            gap: '12px',
            color: 'var(--accent-text)',
            fontWeight: 600,
          }}
        >
          <div
            style={{
              cursor: 'pointer',
              marginTop: '4px',
            }}
            onClick={() => {
              toast.dismiss()
              retryFunction()
            }}
          >
            Try again
          </div>{' '}
          <div
            style={{
              cursor: 'pointer',
              marginTop: '4px',
            }}
            onClick={() => {
              toast.dismiss()
              if (mutation) removeMutationFromCache(mutation.mutationId)
            }}
          >
            Dismiss {mutation && `changes`}
          </div>
        </div>
      </div>,
      {
        duration: Infinity,
      }
    )
  }

  const [queryClient] = useState(
    () =>
      new QueryClient({
        mutationCache: new MutationCache({
          onMutate: (variables, mutation) => {
            console.log('onMutate', mutation)
          },
          onSuccess: () => {
            if (retryingRef.current) {
              const userID = auth.currentUser?.uid
              toast.success('Request completed successfully')
              logErrorToDiscord(
                'n/a',
                getErrorOrSuccessMessageForDiscordLogging({
                  success: true,
                  error: 'Retry successful',
                  userID,
                })
              )
            } //else console.log('Mutation success')

            retryingRef.current = 0
          },
          onError: (error, _variables, _context, mutation) => {
            console.error('QueryClient Mutation Error:', error)

            const errorCode = (error as any).response?.status
            const notUsersFault = notUsersFaultErrors.includes(errorCode)
            const isFirebaseError = error.name === 'FirebaseError'
            const humanErrorMessage = getHumanErrorMessage(
              error,
              (mutation.meta?.['mutationType'] as string) || ''
            )

            // Something on server end or app error
            // We will log it internally and show a user generic message
            // without option to retry
            if (notUsersFault && !isFirebaseError) {
              toast.error(humanErrorMessage, {
                duration: toastDuration,
              })
            } else {
              if (isFirebaseError) {
                // We don't need to store failed firebase errors in the cache
                removeMutationFromCache(mutation.mutationId)
              } else {
                showRetryRequestToast(
                  humanErrorMessage,
                  handleTryAgainMutationCache,
                  mutation as Mutation
                )
              }
            }

            logErrorToDiscord(
              error.stack || 'no stacktrace',
              getErrorOrSuccessMessageForDiscordLogging({
                error,
                humanMessage: humanErrorMessage,
                isSuppressed: isFirebaseError,
              })
            )
          },
        }),
        queryCache: new QueryCache({
          onError: (error: any, query) => {
            // These errors are displayed in the UI, not a toast
            const suppressToastMessage =
              suppressToastErrorsForQueries.includes(
                query.queryKey[0] as string
              ) || error.name === 'FirebaseError'

            console.error('Query Error', error)

            const userID = auth.currentUser?.uid
            const errorMessage = getHumanErrorMessage(error)

            logErrorToDiscord(
              error.stack || 'no stacktrace',
              getErrorOrSuccessMessageForDiscordLogging({
                error: { ...error, message: errorMessage },
                userID,
                isSuppressed: suppressToastMessage,
              })
            )
            console.error('QueryClient', JSON.stringify(error))

            if (suppressToastMessage) return

            // const errorMessage = (error as any).message
            //   ? (error as any).message + '. Please try again.'
            //   : 'An error occurred. Please try again.'

            showRetryRequestToast(errorMessage, handleTryAgainQueryCache)
          },
        }),
        defaultOptions: {
          queries: {
            retry: (errorCount: number, error: any) => {
              // Do not retry for error codes 401, 403, 404, 409, 429, 500, 502, 503, 504

              if (notUsersFaultErrors.includes(error?.response?.status)) {
                return false
              } else return errorCount < maxQueryRetries - 1 // Minus one because errorCount starts at 0
            },
            retryDelay: (retryCount) => Math.min(1000 * 2 ** retryCount, 5000),
          },
          mutations: {
            retry: (errorCount: number, error: any) => {
              // Do not retry for error codes 401, 403, 404, 409, 429, 500, 502, 503, 504
              console.log(
                `Retry ${errorCount + 1}/${maxQueryRetries}...`,
                JSON.stringify(error.message)
              )
              if (
                notUsersFaultErrors.includes(error?.response?.status) ||
                error.name === 'FirebaseError'
              ) {
                return false
              } else {
                return errorCount < maxQueryRetries - 1 // Minus one because errorCount starts at 0
              }
            },
            retryDelay: (retryCount) => Math.min(1000 * 2 ** retryCount, 5000),
          },
        },
      })
  )

  const handleTryAgainMutationCache = useCallback(async () => {
    const mutations = queryClient.getMutationCache().getAll()
    const failedMutations = mutations.filter(
      (mutation) =>
        mutation.state.status === 'error' && !mutation.state.isPaused
    )

    retryingRef.current += 1
    //setToast(null)

    try {
      // for unexpected problems
      await Promise.all(
        failedMutations.map(async (mutation) => {
          try {
            await mutation.execute(mutation.state.variables)
          } catch (error) {}
        })
      )
    } catch (error) {
      console.error('Unexpected error during retries:', error)
    } finally {
      // setErrorToast(null)
    }
  }, [queryClient])

  const handleTryAgainQueryCache = useCallback(async () => {
    const queries = queryClient.getQueryCache().getAll()
    const failedQueries = queries.filter(
      (query) => query.state.status === 'error'
    )

    retryingRef.current += 1
    //setToast(null)

    try {
      // for unexpected problems
      await Promise.all(
        failedQueries.map(async (query) => {
          try {
            await query.fetch()
          } catch (error) {}
        })
      )
    } catch (error) {
      console.error('Unexpected error during retries:', error)
    } finally {
      // setErrorToast(null)
    }
  }, [queryClient])

  const removeMutationFromCache = useCallback(
    (mutationId: number) => {
      const mutationCache = queryClient.getMutationCache()
      const allMutations = mutationCache.getAll()

      // I'm not sure why I cannot just pass the mutation to remove it. Too tired to figure out
      allMutations.forEach((mutation) => {
        if (mutation.mutationId === mutationId) mutationCache.remove(mutation)
      })
    },
    [queryClient]
  )

  const clearQueryCache = useCallback(() => {
    const queries = queryClient.getQueryCache().getAll()
    queries.forEach((query) => {
      query.reset()
    })
  }, [queryClient])

  return (
    <QueryClientProvider client={queryClient}>
      {props.children}
      <Toaster
        containerStyle={{
          justifyContent: 'left',
        }}
        toastOptions={{
          success: {
            iconTheme: {
              primary: 'var(--accent-green)',
              secondary: 'white',
            },
            style: {
              borderRadius: '8px',
            },
          },
          error: {
            iconTheme: {
              primary: 'var(--accent-red)',
              secondary: 'white',
            },
            style: {
              borderRadius: '8px',
              width: '400px',
            },
          },
        }}
      />

      {/* {toast && (
        <Toast
          message={toast.message}
          icon={toast.icon}
          buttons={toast.buttons}
          duration={toast.duration}
          onDismiss={() => setToast(null)}
        />
      )} */}
      {/* Uncomment this line to enable React Query Devtools */}
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  )
}

export default QueryClientWithErrorHandlingProvider

const getHumanErrorMessage = (error: any, mutationType?: string): string => {
  const notUsersFaultErrors = [401, 403, 404, 409, 429, 500, 502, 503, 504]
  const isFirebaseError = error.name === 'FirebaseError'
  const errorCode = error?.response?.status

  if (notUsersFaultErrors.includes(errorCode))
    return 'Ouch, sorry, something went wrong on our end. Please try again later.'

  if (isFirebaseError) {
    return translateAuthErrors(error.code)
  }

  // Server down or no connection: axios has no response to read
  if (error?.isAxiosError && !error.response)
    return "Can't reach Rewod right now. Check your connection and try again."

  if (error.message) {
    if (mutationType === 'postWorkout') {
      return `${error.message}.\nWorkouts haven't been saved.`
    }
    return `${error.message}. Please try again.`
  }

  return 'An error occurred. Please try again.'
}
